<template>
  <!-- showMobileFilterToggle: true — прежний мобильный вариант с кнопкой «Все фильтры» -->
  <MainFilter useSearch v-model="modelFilters" :filtersList="renderFilters" :defaultValues="defaultFilterValues"
              :title="title" :description="props.description"
              :mobileFilters="mobileFilters" :quantity="quantity" :searchPlaceholder="searchPlaceholder"
              :showMobileFilterToggle="false"
              resourceType="clinics" data-current-type="clinic" @updateMobile="onUpdateMobile"
              @updateFilters="handleUpdateFilters">
    <template #bottom-desktop="{ filtersCount, clearFilters, isPreview, modelFilters }">
      <div class="main-filter__bottom _desktop main-filter__bottom--service" v-if="!isPreview">
        <div class="clinic-filter__toggles">
          <ClinicNearbyChip :is-active="nearbyActive" :is-pending="nearbyPending" @toggle="emit('toggleNearby')"/>
          <FormCheckbox v-model="modelFilters.isAlwaysOpen" name="isAlwaysOpen" label="Работает круглосуточно" />
        </div>
        <button v-show="filtersCount" type="button" class="main-filter__clear"
                @click="clearFilters">
          Сбросить все
        </button>
      </div>
    </template>
  </MainFilter>
</template>

<script lang="ts" setup>
import type {TClinicFilter, TClinicFilterList} from "~/types/clinic";
import type {TGroupedItem, TRangeValue} from '~/components/Form/FilterDropdown.vue';
import type {filterItem} from "~/components/MainFilter.vue";

const props = defineProps({
  filters: {
    type: Object as () => TClinicFilterList,
  },
  title: {
    type: String,
    default: ''
  },
  quantity: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  nearbyActive: {
    type: Boolean,
    default: false
  },
  nearbyPending: {
    type: Boolean,
    default: false
  },
});

const emit = defineEmits(['updateOnMobile', 'updateFilters', 'toggleNearby']);

const modelFilters = defineModel<Record<string, string | TRangeValue | boolean | null>>({required: true});
const defaultFilterValues = ref<TClinicFilter>({
  type: 'all',
  city: '',
  district: '',
  metro: '',
  isAlwaysOpen: false,
  search: '',
});

// Временно скрыты type и city — город из шапки, type по умолчанию 'all'.
// const mobileFilters = ['type', 'city', 'district'];
// Прежний мобильный вариант: отдельная строка фильтров + кнопка «Все фильтры» (showMobileFilterToggle: true).
// const mobileFilters = ['district'];
// Сейчас на мобиле — десктопная строка «Район» + «Метро» (см. .clinic-filter в List.vue).
const mobileFilters = ['district'];

const isMobile = ref<boolean>(false);

const renderFilters = computed(() => {
  let result: Record<string, filterItem> = {};

  // result.type = {
  //   type: 'switch',
  //   items: {
  //     all: "Все",
  //     online: "Мгновенная запись"
  //   }
  // };

  if (!props.filters) return {};

  // const cityItems = props.filters.city?.filter(item => (!item.disabled || item.selected)) || [];
  // result.city = {
  //   label: "Город",
  //   items: Object.fromEntries(cityItems.sort((a, b) => a.label.localeCompare(b.label)).map(item => [item.slug, item.label])),
  //   populars: ['moskva', 'sankt-peterburg']
  // };

  const districtItems = props.filters.district?.filter(item => (!item.disabled || item.selected)) || [];
  const parentItems = districtItems.filter(item => !item.parentId);
  const childItems = districtItems.filter(item => item.parentId);

  if (childItems.length === 0) {
    const flatDistricts = Object.fromEntries(districtItems
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(item => ([
          item.slug,
          item.label
        ])));

    result.district = {
      label: "Район",
      items: flatDistricts,
      disabled: !modelFilters.value.city
    };
  } else {

    const groupedDistricts = parentItems.map(parent => {
      const childDistricts = childItems
          .filter(child => child.parentId === parent.id)
          .sort((a, b) => a.label.localeCompare(b.label))
          .map(item => ({
            slug: item.slug,
            label: item.label
          }));

      return {
        label: parent.label,
        slug: parent.slug,
        items: childDistricts,
        isGroup: true
      } as TGroupedItem;
    });

    const districtsWithoutParent = districtItems
        .filter(item => !item.parentId && !parentItems.some(parent => parent.id === item.id))
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(item => ({
          slug: item.slug,
          label: item.label
        }));

    if (districtsWithoutParent.length > 0) {
      groupedDistricts.push({
        id: -1,
        label: 'Районы',
        slug: 'districts',
        items: districtsWithoutParent,
        isGroup: true
      } as TGroupedItem);
    }

    result.district = {
      label: "Район",
      items: groupedDistricts,
      disabled: !modelFilters.value.city
    };
  }

  if (modelFilters.value.city) {
    const metroItems = props.filters.metro?.filter(item => (!item.disabled || item.selected)) || [];
    if (metroItems.length > 0) {
      result.metro = {
        label: "Метро",
        items: Object.fromEntries(metroItems.sort((a, b) => a.label.localeCompare(b.label)).map(item => [item.slug, item.label])),
        disabled: !modelFilters.value.city
      };
    }
  }

  return result;
})

const searchPlaceholder = computed(() => {
  return 'Поиск клиник по названию';
})

const onUpdateMobile = (newFilters: any) => {
  if(window.innerWidth > 768) return;
  emit('updateOnMobile', newFilters);
}

const handleUpdateFilters = (updatedFilters: Record<string, any>) => {
  emit('updateFilters', updatedFilters);
}

onMounted(() => {
  isMobile.value =  window.innerWidth <= 768;
})
</script>

<style lang="scss">
.clinic-filter__toggles {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
</style>